<template>
  <div class="project">
    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
        <img id="navbar-logo" src="../../assets/projects/pp20/paaspop20.jpg" alt="Logo">
      </div>
    </nav>

    <!-- Header Section -->
    <section class="project-header">
      <h1>Paaspop 2020: Mini Games Platform</h1>
      <p>
        In 2020, our team embarked on an exciting project for Paaspop, with a vision for creating a scalable platform for future events. Our focus was on developing web-based mini-games that could be showcased on large screens, allowing users to engage through interactive, multiple-choice experiences.
      </p>
    </section>

    <!-- 3D Racing Game Section -->
    <section class="project-section">
      <h2>3D Racing Game</h2>
      <p>
        One of the highlights of our Paaspop 2020 project was the creation of a thrilling 3D racing game inspired by the Dutch carnival classic, "Kamelen Race." This modern twist on a traditional game brought excitement to the event. Below, you can delve into the technical details and behind-the-scenes of the 3D version.
      </p>
        <br>
      <!-- 3D Racing Game Image -->
      <div class="columns is-multiline is-full">
      <div class="column is-full">
      <img class="project-image" src="../../assets/projects/pp20/threejs.gif" alt="3D Racing Game Image" />
      </div>  
      </div>

      <!-- Code Snippet 1: Setting up the 3D Scene -->
      <div v-highlight>
        <pre class="language-javascript">
          <code>
            // Setting up the 3D scene
            var renderer = new THREE.WebGLRenderer({ antialias: true });
            renderer.setSize(window.innerWidth, window.innerHeight);
            renderer.setClearColor(0x0000ff);
            this.document.body.appendChild(renderer.domElement);
            var scene = new THREE.Scene();

            // Configuring the camera
            const FOV = 45;
            const ASPECT = window.innerWidth / window.innerHeight;
            const NEAR = 0.1;
            const FAR = 2000;
            var camera = new THREE.PerspectiveCamera(FOV, ASPECT, NEAR, FAR);
            camera.up.set(0, -0.5, 2);
            camera.rotation.x = -Math.PI / 4;
            scene.add(camera);

            // Setting up lighting
            var ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
            scene.add(ambientLight);
            var light = new THREE.DirectionalLight(0xffffff, 1);
            light.position.set(0, -10, 5);
            light.castShadow = true;
            scene add(light);

            // Adding orbit controls
            var controls = new THREE.OrbitControls(camera, renderer.domElement);
            controls.minDistance = 80;
            controls.update();
          </code>
        </pre>
      </div>
<br>
      <p>
        The 3D racing game began with setting up the Three.js scene, camera, lighting, and orbit controls. These components laid the foundation for an immersive 3D environment where the camel (bus) racing took place.
      </p>
    </section>

    <!-- Handling Camel Movement in 3D Version (Code Snippet 2) -->
    <section class="project-section">
      <h2>Handling Camel Movement in 3D Version</h2>
      <p>
        The magic behind the camel (bus) racing game in the 3D version involved geometries, animations, and trigonometric calculations. This snippet showcases how we controlled camel movement as they raced around the circular track.
      </p>
<br>
      <!-- Code Snippet 2: Handling Camel Movement in 3D Version -->
      <div v-highlight>
        <pre class="language-javascript">
          <code>

            // Geometries
            groundWidth = 80;
            var geometry = new THREE.PlaneGeometry(groundWidth, groundWidth);
            var groundMat = new THREE.MeshLambertMaterial({
              color: 0x00ff00,
              side: THREE.DoubleSide,
            });
            var ground = a THREE.Mesh(geometry, groundMat);
            ground.position.z = -0.1;
            scene.add(ground);

            phi = 90;
            var Y = 24;
            var B = 26;
            var R = 28;
            var G = 30;
            var randomSpeed = Math.random(5, 10);
            myButton.addEventListener("click", function () {
              test();
            });

            // Camel animation function
            function test() {
              phi += 0.5;
              console.log(phi);
              rad = (2 * Math.PI) / 360 * phi;

              // Position and rotate the camels
              figureYellow.position.x = Y * Math.cos(rad);
              figureYellow.position.y = Y * Math.sin(rad);
              figureYellow.rotation.z = rad + (0.5 * Math.PI);

              figureBlue.position.x = B * Math.cos(rad);
              figureBlue.position.y = B * Math.sin(rad);
              figureBlue.rotation.z = rad + (0.5 * Math.PI);

              figureRed.position.x = R * Math.cos(rad);
              figureRed.position.y = R * Math.sin(rad);
              figureRed.rotation.z = rad + (0.5 * Math.PI);

              figureGreen.position.x = G * Math.cos(rad);
              figureGreen.position.y = G * Math.sin(rad);
              figureGreen.rotation.z = rad + (0.5 * Math.PI);

              setTimeout(test, 10);
            }
          </code>
        </pre>
      </div>
    </section>

    <!-- 2D Racing Game Section -->
    <section class="project-section">
      <h2>2D Racing Game</h2>
      <p>
        In addition to the 3D version, our project also included a 2D racing game. This game allowed users to experience the thrill of bus racing with a different perspective.
      </p> <br>
      <div class="columns is-multiline">
      <div class="column is-full">
        <img class="project-image" src="../../assets/projects/pp20/finalversion.png" alt="2D Racing Game Image" />
      </div>  
      </div>
      <!-- 2D Racing Game Video -->

      <p>
        Participants could place their votes and watch as the buses raced towards the finish line. Here's a glimpse of how the 2D racing game worked:
      </p>
      <br>
      <!-- Code Snippet 3: 2D Racing Game - Bus Constructors -->
      <div v-highlight>
        <pre class="language-javascript">
          <code>
            // Constructors for Bus and Medal classes
            class Bus {
              constructor(busId, name, speed, color, votes) {
                this.busId = busId;
                this.name = name;
                this.speed = speed;
                this.color = color;
                this.votes = votes;
              }
            }

            class Medal {
              constructor(medalId, name) {
                this.medalId = medalId;
                this.name = name;
              }
            }
          </code>
        </pre>
      </div>
    </section>

    <!-- Handling Bus Movement in 2D Version (Code Snippet 4) -->
    <section class="project-section">
      <h2>Handling Bus Movement in 2D Version</h2>
      <p>
        The 2D racing game involved bus movement control, random speed generation, and determining the winners. Let's dive into how we made the buses race and declared the results.
      </p>
    <br>

      
      <!-- Code Snippet 4: Handling Bus Movement in 2D Version -->
      <div v-highlight>
        <pre class="language-javascript">
          <code class="language-javascript">
            // Function to move buses
            function move(bus_id) {
              return new Promise(resolve => {
                setTimeout(() => {
                  let originalPos = parseInt(document.getElementById(bus_id).style.left, 10);
                  var randomSpeedMultiplier = Math.floor((Math.random() * 20) + 1);
                  var step = 1 * randomSpeedMultiplier;
                  let newPos = originalPos + step;
                  document.getElementById(bus_id).style.left = newPos.toString() + "px";
                  resolve(newPos);
                }, 100);
              });
            }

            // Asynchronous function to start moving buses
            async function start_moving(bus_id) {
              let bus = await move(bus_id);
              const distance = window.innerWidth / 100 * 82;
              if (bus > distance) {
                // Logic to handle winners
                // ...
              }
            }
          </code>
        </pre>
      </div>
    </section>
  </div>
</template>

<script>
import VueCodeHighlight from 'vue-code-highlight';
import 'vue-code-highlight/themes/prism.css'; // Include the theme CSS

export default {
  directives: {
    'highlight': VueCodeHighlight,
  },
};
</script>

<style scoped>
  
@import 'project.css';

</style>